import { createApiClient } from "./apiClient";
import {
  ApiResponse,
  CategoryCreateRequest,
  CategoryMappingFieldCreateRequest,
  CategoryMappingFieldResponse,
  CategoryMappingFieldUpdateRequest,
  CategoryReviewSaveRequest,
  CategoryUpdateRequest,
  OrganizationLookup,
  SubCategoryCreateRequest,
  TeamAssignmentPageResponse,
  TeamAssignmentResponse,
  TeamAssignmentUpdateRequest,
  TeamCreateRequest,
  TeamResponse,
  TertiaryCategoryCreateRequest,
  TertiaryCategoryResponse,
  TicketingCategoryDTO,
  TicketingSubCategoryDTO,
} from "../types/ticketingConfig.types";

// Base path is relative to the shared axios baseURL (already includes /api),
// matching TicketingConfigController's @RequestMapping("/api/ticketing/config").
const BASE = "/ticketing/config";

export interface TeamAssignmentQueryParams {
  search?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  sortOrder?: string;
}

export type GetTeamsOptions = {
  excludeWithoutOpenTickets?: boolean;
};

export interface CategoryMappingFieldPath {
  categoryId: number;
  subcategoryId?: number;
  tertiaryId?: number;
}

export const ticketingConfigApi = {
  // Teams
  getTeams: async (options: GetTeamsOptions = {}): Promise<TeamResponse[]> => {
    const api = createApiClient();
    const res = await api.get<ApiResponse<TeamResponse[]>>(`${BASE}/teams`,
      options.excludeWithoutOpenTickets === true
        ? { params: { excludeWithoutOpenTickets: true } }
        : undefined
    );
    return res.data.data ?? [];
  },
  createTeam: async (body: TeamCreateRequest): Promise<TeamResponse> => {
    const api = createApiClient();
    const res = await api.post<ApiResponse<TeamResponse>>(`${BASE}/teams`, body);
    return res.data.data;
  },
  setTeamStatus: async (id: number, active: boolean): Promise<TeamResponse> => {
    const api = createApiClient();
    const res = await api.patch<ApiResponse<TeamResponse>>(
      `${BASE}/teams/${id}/status`,
      null,
      { params: { active } }
    );
    return res.data.data;
  },

  // Team Assignment
  getTeamAssignments: async (
    params: TeamAssignmentQueryParams
  ): Promise<TeamAssignmentPageResponse> => {
    const api = createApiClient();
    const res = await api.get<ApiResponse<TeamAssignmentPageResponse>>(
      `${BASE}/team-assignments`,
      { params }
    );
    return { ...(res.data.data ?? ({} as TeamAssignmentPageResponse)), content: res.data.data?.content ?? [] };
  },
  getTeamAssignment: async (id: number): Promise<TeamAssignmentResponse> => {
    const api = createApiClient();
    const res = await api.get<ApiResponse<TeamAssignmentResponse>>(
      `${BASE}/team-assignments/${id}`
    );
    return res.data.data;
  },
  updateTeamAssignment: async (
    id: number,
    body: TeamAssignmentUpdateRequest
  ): Promise<TeamAssignmentResponse> => {
    const api = createApiClient();
    const res = await api.patch<ApiResponse<TeamAssignmentResponse>>(
      `${BASE}/team-assignments/${id}`,
      body
    );
    return res.data.data;
  },
  getOrganizations: async (): Promise<OrganizationLookup[]> => {
    const api = createApiClient();
    const res = await api.get<ApiResponse<OrganizationLookup[]>>(`${BASE}/organizations`);
    return res.data.data ?? [];
  },

  // Category Mapping
  getCategories: async (): Promise<TicketingCategoryDTO[]> => {
    const api = createApiClient();
    const res = await api.get<ApiResponse<TicketingCategoryDTO[]>>(`${BASE}/categories`);
    return res.data.data ?? [];
  },
  createCategory: async (body: CategoryCreateRequest): Promise<TicketingCategoryDTO> => {
    const api = createApiClient();
    const res = await api.post<ApiResponse<TicketingCategoryDTO>>(`${BASE}/categories`, body);
    return res.data.data;
  },
  updateCategory: async (
    id: number,
    body: CategoryUpdateRequest
  ): Promise<TicketingCategoryDTO> => {
    const api = createApiClient();
    const res = await api.patch<ApiResponse<TicketingCategoryDTO>>(
      `${BASE}/categories/${id}`,
      body
    );
    return res.data.data;
  },
  getSubCategories: async (categoryId: number): Promise<TicketingSubCategoryDTO[]> => {
    const api = createApiClient();
    const res = await api.get<ApiResponse<TicketingSubCategoryDTO[]>>(
      `${BASE}/categories/${categoryId}/subcategories`
    );
    return res.data.data ?? [];
  },
  createSubCategories: async (
    categoryId: number,
    body: SubCategoryCreateRequest
  ): Promise<TicketingSubCategoryDTO[]> => {
    const api = createApiClient();
    const res = await api.post<ApiResponse<TicketingSubCategoryDTO[]>>(
      `${BASE}/categories/${categoryId}/subcategories`,
      body
    );
    return res.data.data ?? [];
  },
  updateSubCategory: async (
    id: number,
    body: CategoryUpdateRequest
  ): Promise<TicketingSubCategoryDTO> => {
    const api = createApiClient();
    const res = await api.patch<ApiResponse<TicketingSubCategoryDTO>>(
      `${BASE}/subcategories/${id}`,
      body
    );
    return res.data.data;
  },
  getTertiaryCategories: async (
    subCategoryId: number
  ): Promise<TertiaryCategoryResponse[]> => {
    const api = createApiClient();
    const res = await api.get<ApiResponse<TertiaryCategoryResponse[]>>(
      `${BASE}/subcategories/${subCategoryId}/tertiary`
    );
    return res.data.data ?? [];
  },
  createTertiaryCategories: async (
    subCategoryId: number,
    body: TertiaryCategoryCreateRequest
  ): Promise<TertiaryCategoryResponse[]> => {
    const api = createApiClient();
    const res = await api.post<ApiResponse<TertiaryCategoryResponse[]>>(
      `${BASE}/subcategories/${subCategoryId}/tertiary`,
      body
    );
    return res.data.data ?? [];
  },
  updateTertiary: async (
    id: number,
    body: CategoryUpdateRequest
  ): Promise<TertiaryCategoryResponse> => {
    const api = createApiClient();
    const res = await api.patch<ApiResponse<TertiaryCategoryResponse>>(
      `${BASE}/tertiary/${id}`,
      body
    );
    return res.data.data;
  },
  reviewSave: async (body: CategoryReviewSaveRequest): Promise<TicketingCategoryDTO> => {
    const api = createApiClient();
    const res = await api.post<ApiResponse<TicketingCategoryDTO>>(`${BASE}/review`, body);
    return res.data.data;
  },
  addField: async (
    body: CategoryMappingFieldCreateRequest
  ): Promise<CategoryMappingFieldResponse> => {
    const api = createApiClient();
    const res = await api.post<ApiResponse<CategoryMappingFieldResponse>>(
      `${BASE}/fields`,
      body
    );
    return res.data.data;
  },
  updateField: async (
    id: number,
    body: CategoryMappingFieldUpdateRequest
  ): Promise<CategoryMappingFieldResponse> => {
    const api = createApiClient();
    const res = await api.put<ApiResponse<CategoryMappingFieldResponse>>(
      `${BASE}/fields/${id}`,
      body
    );
    return res.data.data;
  },
  setFieldStatus: async (id: number, active: boolean): Promise<CategoryMappingFieldResponse> => {
    const api = createApiClient();
    const res = await api.patch<ApiResponse<CategoryMappingFieldResponse>>(
      `${BASE}/fields/${id}/status`,
      null,
      { params: { active } }
    );
    return res.data.data;
  },
  getFields: async (
    path: CategoryMappingFieldPath,
    includeInactive = false
  ): Promise<CategoryMappingFieldResponse[]> => {
    const api = createApiClient();
    const res = await api.get<ApiResponse<CategoryMappingFieldResponse[]>>(
      `${BASE}/fields`,
      { params: { ...path, includeInactive } }
    );
    return res.data.data ?? [];
  },
};

export default ticketingConfigApi;
